import { Link, useNavigate } from "react-router-dom";
import { FileQuestion, AlertTriangle, Send, MessageCircleReply, Clock, ThumbsUp, HelpCircle, BookmarkPlus } from "lucide-react";
import { fmtDate } from "@/lib/api";
import { CoveragePill, ConfidencePill } from "@/components/Badges";
import { Markdown } from "@/components/Markdown";
import { pick, TranslateToggle } from "@/components/Translate";
import { t } from "@/i18n";
import { Tt } from "@/components/Tt";

const lastUpdated = (cites) => cites.reduce((m, c) => (c.updated_at > m ? c.updated_at : m), "");

function Citations({ citations }) {
  const seen = {};
  citations.forEach((c) => { seen[c.sop_id] = seen[c.sop_id] || c; });
  return (
    <div className="stack-sm" data-testid="answer-citations">
      <p className="mono-label">{t("Sources")}</p>
      <div className="flex flex-wrap gap-2">
        {Object.values(seen).map((c, i) => (
          <Link key={i} to={c.property_id ? "/properties" : `/sops/${c.sop_id}`} className="chip chip-accent citation max-w-full overflow-hidden text-ellipsis whitespace-nowrap inline-block" title={c.quote} data-testid={`citation-chip-${i}`}>
            [{c.ref.replace("SOP", "OI")}] <Tt text={c.sop_title} />{c.section ? ` · ${c.section}` : ""}
          </Link>
        ))}
      </div>
      {citations.filter((c) => c.quote).slice(0, 3).map((c, i) => (
        <p key={i} className="text-xs faint border-l-2 border-[var(--border-active)] pl-2 italic">“{c.quote}” — {c.ref.replace("SOP", "OI")}</p>
      ))}
    </div>
  );
}

export function AnswerCard({ q, onEscalate, onReact, onPromote, promoteLabel, promoted, original = false, onToggleOriginal }) {
  const notFound = q.coverage === "not_found";
  const nav = useNavigate();
  if (q.self_manager && notFound) return (
    <div className="bubble-in stack-sm rise max-w-[88%]" data-testid={`answer-card-${q.id}`}>
      <div className="banner banner-info" data-testid="answer-not-in-oi">
        <FileQuestion size={18} className="text-[var(--status-info)] shrink-0" />
        <div className="stack-sm"><b>{t("Not in OI base yet")}</b>
          <button className="btn btn-primary btn-sm self-start" onClick={() => nav(`/capture?title=${encodeURIComponent(q.question)}`)} data-testid={`answer-add-answer-button-${q.id}`}>{t("Add answer")}</button>
        </div>
      </div>
    </div>
  );
  return (
    <div className="bubble-in stack-sm rise max-w-[88%]" data-testid={`answer-card-${q.id}`}>
      <div className="flex flex-wrap items-center gap-2">
        <CoveragePill coverage={q.coverage} />{!notFound && <ConfidencePill level={q.confidence} />}
        {!notFound && <span className="chip chip-muted" data-testid="answer-last-updated"><Clock size={12} /> {t("OI updated")} {fmtDate(lastUpdated(q.citations))}</span>}
      </div>
      {notFound ? (
        <div className="banner banner-err" data-testid="answer-no-answer-banner">
          <FileQuestion size={18} className="text-[var(--status-error)] shrink-0" />
          <div><b>{q.answer_markdown && q.answer_markdown !== "No answer found in current knowledge base." ? pick(q, "answer_markdown", original) : t("No answer found in current knowledge base.")}</b>{!q.assigned_name && <p className="text-sm muted mt-1">{t("Sent to the owner.")}</p>}</div>
        </div>
      ) : <Markdown testid="answer-markdown">{pick(q, "answer_markdown", original)}</Markdown>}
      {q.conflict && <div className="banner banner-warn" data-testid="answer-conflict-banner"><AlertTriangle size={18} className="text-[var(--status-warning)] shrink-0" /><div><b>{t("Possible contradiction")}:</b> {pick(q, "conflict", original)}</div></div>}
      {q.coverage === "partial" && q.missing && <div className="banner banner-info" data-testid="answer-missing-banner"><FileQuestion size={18} className="text-[var(--status-info)] shrink-0" /><div><b>{t("Not covered")}:</b> {pick(q, "missing", original)} — {t("routed to the owner.")}</div></div>}
      {q.citations.length > 0 && <Citations citations={q.citations} />}
      {q.suggestion && (
        <div className="card !p-3 stack-sm" data-testid={`answer-suggestion-${q.id}`}>
          <p className="text-sm muted">{t("Meanwhile, here's something you can use:")}</p>
          <Link to={`/sops/${q.suggestion.sop_id}`} className="font-semibold hover:underline" data-testid={`answer-suggestion-link-${q.id}`}><Tt text={q.suggestion.title} /></Link>
          <div className="text-sm"><Markdown>{q.suggestion.markdown}</Markdown></div>
        </div>
      )}
      {q.owner_reply && (
        <div className="banner banner-info" data-testid="answer-owner-reply">
          <MessageCircleReply size={18} className="text-[var(--status-info)] shrink-0" />
          <div><b>{t("Owner reply")}</b> <span className="text-xs faint">({t(q.sop_update_id ? "OI update pending" : "not in an OI yet")})</span><p className="mt-1" data-testid={`answer-owner-reply-text-${q.id}`}>{pick(q, "owner_reply", original)}</p></div>
        </div>
      )}
      {onToggleOriginal && <TranslateToggle q={q} original={original} onToggle={() => onToggleOriginal(q.id)} />}
      <div className="flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs faint">{t(q.routed ? (q.status === "open" ? "Sent to owner" : q.status === "resolved" ? "Owner replied" : "Closed") : "Delivered")}</span>
        <div className="flex items-center gap-1.5">
          {!notFound && onReact && <Reactions q={q} onReact={onReact} />}
          {q.owner_reply && onPromote && <button className="btn btn-ghost btn-sm" disabled={promoted} onClick={() => onPromote(q)} data-testid={`answer-promote-button-${q.id}`}><BookmarkPlus size={13} /> {promoted ? t("Submitted") : promoteLabel}</button>}
          {!q.routed && !q.self_manager && <button className="btn btn-ghost btn-sm" onClick={() => onEscalate(q.id)} data-testid={`answer-escalate-button-${q.id}`}><Send size={13} /> {t("Send to owner")}</button>}
        </div>
      </div>
    </div>
  );
}

function Reactions({ q, onReact }) {
  const opts = [["helpful", ThumbsUp, "Helpful"], ["unclear", HelpCircle, "Unclear"]];
  return opts.map(([key, Icon, label]) => {
    const on = q.reaction === key;
    return (
      <button key={key} onClick={() => onReact(q.id, on ? null : key)} title={t(label)} aria-pressed={on}
        className={`h-8 px-2.5 rounded-full inline-flex items-center gap-1 text-xs font-medium transition-colors ${on ? (key === "helpful" ? "bg-[var(--status-ok)] text-white" : "bg-[var(--status-warning)] text-white") : "bg-[var(--bg-primary)] faint hover:text-[var(--text-primary)]"}`}
        data-testid={`answer-react-${key}-${q.id}`}>
        <Icon size={14} /> {key === "unclear" && t(label)}
      </button>
    );
  });
}
